import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";

export interface Project {
  id: string;
  organization_id: string | null;
  owner_user_id: string;
  name: string;
  description: string | null;
  created_at: string;
  updated_at: string;
  runs_count?: number;
  last_run_at?: string;
  avg_clarity_score?: number;
}

export interface CreateProjectData {
  name: string;
  description?: string;
  organization_id?: string;
}

export function useProjects() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchProjects = useCallback(async () => {
    if (!user) {
      setProjects([]);
      setLoading(false);
      return;
    }

    try {
      const { data: projectsData, error: projectsError } = await supabase
        .from("projects")
        .select("*")
        .order("updated_at", { ascending: false });

      if (projectsError) throw projectsError;

      const projectIds = projectsData.map((p) => p.id);

      if (projectIds.length > 0) {
        const { data: runsData, error: runsError } = await supabase
          .from("runs")
          .select("project_id, clarity_score, created_at")
          .in("project_id", projectIds)
          .eq("status", "succeeded");

        if (runsError) throw runsError;

        const projectsWithStats = projectsData.map((project) => {
          const projectRuns = runsData?.filter((r) => r.project_id === project.id) || [];
          const runsCount = projectRuns.length;
          const lastRun = projectRuns.sort((a, b) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
          )[0];
          const avgScore = projectRuns.length > 0
            ? Math.round(
                projectRuns.reduce((sum, r) => sum + (Number(r.clarity_score) || 0), 0) / projectRuns.length
              )
            : undefined;

          return {
            ...project,
            runs_count: runsCount,
            last_run_at: lastRun?.created_at,
            avg_clarity_score: avgScore,
          };
        });

        setProjects(projectsWithStats);
      } else {
        setProjects(projectsData);
      }
    } catch (error) {
      console.error("Failed to fetch projects:", error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const createProject = useCallback(async (data: CreateProjectData) => {
    if (!user) return null;

    try {
      const { data: projectData, error } = await supabase
        .from("projects")
        .insert({
          name: data.name,
          description: data.description || null,
          organization_id: data.organization_id || null,
          owner_user_id: user.id,
        })
        .select()
        .single();

      if (error) throw error;

      toast({
        title: "Project created",
        description: `${data.name} has been created.`,
      });

      await fetchProjects();
      return projectData;
    } catch (error: any) {
      console.error("Failed to create project:", error);
      toast({
        title: "Failed to create project",
        description: error.message || "Please try again.",
        variant: "destructive",
      });
      return null;
    }
  }, [user, fetchProjects, toast]);

  const updateProject = useCallback(async (id: string, updates: Partial<CreateProjectData>) => {
    try {
      const { error } = await supabase
        .from("projects")
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq("id", id);

      if (error) throw error;

      toast({
        title: "Project updated",
        description: "Project settings have been saved.",
      });

      await fetchProjects();
      return true;
    } catch (error: any) {
      console.error("Failed to update project:", error);
      toast({
        title: "Update failed",
        description: error.message || "Please try again.",
        variant: "destructive",
      });
      return false;
    }
  }, [fetchProjects, toast]);

  const deleteProject = useCallback(async (id: string) => {
    try {
      const { error } = await supabase
        .from("projects")
        .delete()
        .eq("id", id);

      if (error) throw error;

      toast({
        title: "Project deleted",
        description: "Project has been deleted.",
      });

      await fetchProjects();
      return true;
    } catch (error: any) {
      console.error("Failed to delete project:", error);
      toast({
        title: "Delete failed",
        description: error.message || "Please try again.",
        variant: "destructive",
      });
      return false;
    }
  }, [fetchProjects, toast]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  return {
    projects,
    loading,
    createProject,
    updateProject,
    deleteProject,
    refetch: fetchProjects,
  };
}
